import { addMonths, DateOnly } from '@if/core/date'
import { dateOnly, double, InferSubmit, object } from '@if/core/yup'
import { ErrorAlert, FormMethods, useFormDialog, useIfUser } from '@if/mui'
import { CircularProgress, Stack, styled } from '@mui/material'
import { DateCalendar } from '@mui/x-date-pickers'
import { useComputeCatchUpAmountOfOverdueForDays } from '@src/api/loan/loan-api'
import { getToday } from '@src/services/DateTimeProvider'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { EPaymentMethod } from '@src/types/constants'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { CalendarPlus } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGetCurrentOverdueAmount } from '../../LoanCalendar-hooks'
import { EditPaymentSchemaType } from '../EditPayments-schema'
import ReturnToCalendarAction, { RETURN_TO_CALENDAR, ToolResult } from '../EditPayments-tool'
import CatchUpAmountOfOverdueForDateDaySlot, {
  CatchUpAmountOfOverdueForDateDaySlotProps,
  DAY_WIDTH_PX,
} from './CatchUpAmountOfOverdueForDateDaySlot'

/**
 * The day the agent picks and what catching up on it costs. The amount is not typed — it is
 * read off the backend's per-day answer the moment a day is picked, so Confirm can never
 * apply a figure the calendar was not showing.
 */
const catchUpAmountOfOverdueForDateSchema = object({
  effectiveDate: dateOnly().required().default(null),
  amount: double().required().moreThan(0).default(null),
})

type CatchUpAmountOfOverdueForDateSchemaType = InferSubmit<typeof catchUpAmountOfOverdueForDateSchema>

const StyledDateCalendar = styled(DateCalendar)({
  height: '515px',
  maxHeight: '515px',
  width: '100%',
  '.MuiPickersSlideTransition-root, .MuiDayCalendar-loadingContainer': {
    minHeight: '430px',
  },
  '.MuiDayCalendar-weekDayLabel': {
    width: DAY_WIDTH_PX,
  },
  '.MuiPickersCalendarHeader-root': {
    marginTop: 0,
  },
})

interface CatchUpAmountOfOverdueForDateBodyProps {
  form: FormMethods<CatchUpAmountOfOverdueForDateSchemaType>
  simulation: SignedCalendarSimulation
}

const CatchUpAmountOfOverdueForDateBody = ({ form, simulation }: CatchUpAmountOfOverdueForDateBodyProps) => {
  const { t } = useTranslation()
  const { control, setValue } = form
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments
  const hasCurrentOverdueAmount = useGetCurrentOverdueAmount(simulation.simulation)

  const effectiveDate = useWatch({ control, name: 'effectiveDate' })

  const today = useMemo(() => getToday(), [])
  const loanActivationDate = simulation.simulation.fullLoan.loan.activationOn
  const minDate = useMemo(
    () =>
      SafePaymentActionDateService.getInstance()
        .getMinPaymentDate(EPaymentMethod.PreAuthorizedDebit, loanActivationDate, canBypassAcpLock)
        .toDate(),
    [canBypassAcpLock, loanActivationDate],
  )
  const shouldDisableDate = useCallback((day: Date) => day.getTime() < minDate.getTime(), [minDate])

  const lastPaymentOn = simulation.simulation.fullLoan.loan.lastPaymentOn
  const maxDateOnly = useMemo(() => {
    const maxInterval = addMonths(today, 2)
    return maxInterval.getTime() < lastPaymentOn.getTime() ? maxInterval : lastPaymentOn
  }, [today, lastPaymentOn])

  const {
    data: lateAmountForDays,
    isPending,
    error: errorApi,
  } = useComputeCatchUpAmountOfOverdueForDays(simulation.simulation, today, maxDateOnly)

  // Picking a day answers both fields at once — the day and what it costs — so the two can
  // never drift apart. A day the backend priced at nothing keeps Confirm shut through the
  // schema's `moreThan(0)` rather than through a gate of its own.
  const handleDateChange = useCallback(
    (day: Date | null) => {
      if (!day) return
      const picked = DateOnly.fromDate(day)
      setValue('effectiveDate', picked, { shouldValidate: true })
      setValue('amount', lateAmountForDays?.get(picked.toString()) ?? 0, { shouldValidate: true })
    },
    [lateAmountForDays, setValue],
  )

  const error = useMemo(() => {
    if (!isPending && (!lateAmountForDays || lateAmountForDays.size === 0))
      return t('loan.payment.catchUpAmountOfOverdueForDate.errors.noValidDatesFound')
    if (!hasCurrentOverdueAmount) return t('loan.payment.catchUpAmountOfOverdueForDate.errors.simulationNotValid')
    return errorApi
  }, [errorApi, isPending, lateAmountForDays, hasCurrentOverdueAmount, t])

  return (
    <Stack spacing={1}>
      {error && <ErrorAlert error={error} />}
      <StyledDateCalendar
        value={effectiveDate?.toDate() ?? null}
        onChange={handleDateChange}
        loading={isPending}
        minDate={today.toDate()}
        maxDate={maxDateOnly.toDate()}
        shouldDisableDate={shouldDisableDate}
        disabled={error !== null}
        renderLoading={() => <CircularProgress />}
        showDaysOutsideCurrentMonth
        slots={{
          day: CatchUpAmountOfOverdueForDateDaySlot as never,
        }}
        slotProps={{
          day: {
            lateAmountForDays: lateAmountForDays,
          } satisfies CatchUpAmountOfOverdueForDateDaySlotProps as never,
        }}
      />
    </Stack>
  )
}

interface UseCatchUpAmountOfOverdueForDateDialogProps {
  simulation: SignedCalendarSimulation
}

/** Stages the one pre-authorized payment that clears the whole overdue on a chosen day. */
export const useCatchUpAmountOfOverdueForDateDialog = ({ simulation }: UseCatchUpAmountOfOverdueForDateDialogProps) => {
  const { t } = useTranslation()

  return useFormDialog<CatchUpAmountOfOverdueForDateSchemaType, void, ToolResult<EditPaymentSchemaType>>({
    icon: CalendarPlus,
    title: t('loan.payment.catchUpAmountOfOverdueForDate.title'),
    confirmLabel: t('loan.payment.catchUpAmountOfOverdueForDate.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    schema: catchUpAmountOfOverdueForDateSchema,
    successMessage: false,
    additionalActions: (ctx) => <ReturnToCalendarAction onReturn={() => ctx.close(RETURN_TO_CALENDAR)} />,
    content: ({ form }) => <CatchUpAmountOfOverdueForDateBody form={form} simulation={simulation} />,
    onSubmit: (data) => ({
      id: crypto.randomUUID(),
      amount: data.amount,
      effectiveDate: data.effectiveDate,
      paymentMethod: EPaymentMethod.PreAuthorizedDebit,
      sendSmsReminder: true,
      note: null,
      isDeleted: false,
      isLocked: false,
    }),
  })
}
